import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { useLang } from '../../i18n';
import { ManifestoWords } from './ManifestoScrub';

/**
 * «Scomposti a tavola»: il metodo non si elenca, si scompone.
 *
 * Le quattro regole erano una lista con titolo e testo aperti tutti insieme —
 * quasi due schermate di telefono per un concetto che sta in una riga. Ora i
 * quattro titoli stanno in fila come posate a un coperto, e la regola si legge
 * una alla volta dentro la sezione: il concetto viene agito invece che elencato,
 * e nessun contenuto e' andato perso. Sotto, il ponte verso il gioco che su
 * quelle stesse quattro regole assegna il punteggio.
 */
export const MethodBuild = () => {
  const { t } = useLang();
  const rules = t('home.rules');
  const [attiva, setAttiva] = useState(0);
  const ridotta = useReducedMotion();
  const tabs = useRef([]);
  const [, testo] = rules[attiva] || rules[0];

  // frecce per muoversi tra le regole senza mouse, come vuole un tablist
  const onKey = (e) => {
    const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const next = (attiva + d + rules.length) % rules.length;
    setAttiva(next);
    tabs.current[next]?.focus();
  };

  return (
    <section data-testid="home-method" className="bg-carta-2 py-16 md:py-24">
      <div className="wrap">
        {/* Il credo, poi come si mette in pratica: erano due sezioni con due
            cornici e due intestazioni che dicevano la stessa cosa. */}
        <ManifestoWords />

        <div className="mt-20 max-w-[32ch] md:mt-28">
          <h2 className="text-[30px] leading-[1.06] md:text-[40px] lg:text-[52px]">{t('home.scompostoTitle')}</h2>
          <p className="prosa mt-6">{t('home.scompostoLead')}</p>
        </div>

        <div className="mt-10 md:mt-14">
          <div
            role="tablist"
            aria-label={t('home.scompostoTitle')}
            onKeyDown={onKey}
            className="grid grid-cols-2 gap-2 md:grid-cols-4"
          >
            {rules.map(([titoloRegola], i) => {
              const on = i === attiva;
              return (
                <button
                  key={titoloRegola}
                  ref={(el) => {
                    tabs.current[i] = el;
                  }}
                  type="button"
                  role="tab"
                  id={`regola-tab-${i}`}
                  aria-selected={on}
                  aria-controls="regola-pannello"
                  tabIndex={on ? 0 : -1}
                  onClick={() => setAttiva(i)}
                  data-testid={`regola-${i}`}
                  className={`min-h-[64px] px-4 py-4 text-left font-display text-[18px] leading-snug transition-colors duration-300 md:text-[24px] ${
                    on ? 'bg-inchiostro text-carta' : 'bg-carta hover:bg-carta-3'
                  }`}
                >
                  {titoloRegola}
                </button>
              );
            })}
          </div>

          <div
            id="regola-pannello"
            role="tabpanel"
            aria-labelledby={`regola-tab-${attiva}`}
            data-testid="regola-pannello"
            className="min-h-[96px] pt-7"
          >
            {/* key sul contenuto: il pannello si riscrive a ogni regola senza
                AnimazionePresence, quindi non c'e' un'uscita che possa restare
                appesa e il testo e' sempre nel DOM */}
            <motion.div
              key={attiva}
              initial={ridotta ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="prosa max-w-[54ch] text-[18px]">{testo}</p>
            </motion.div>
          </div>
        </div>

        <Link to="/componi" data-testid="home-build-cta" className="btn btn-solid mt-10">
          {t('nav.build')}
          <ArrowUpRight size={15} strokeWidth={2} />
        </Link>

        {/* AD-7: il payoff chiude una sezione, non la apre. Chiudeva il
            manifesto quando era sezione a se'; ora sigilla quella unica. */}
        <p
          data-testid="home-payoff"
          className="accento mt-16 text-right text-[24px] text-marsala md:mt-20 md:text-[36px]"
        >
          {t('common.payoff')}
        </p>
      </div>
    </section>
  );
};

/** Due strade: al passaggio del puntatore una si allarga e l'altra si stringe. */
export const DualPath = () => {
  const { t } = useLang();
  const paths = [
    ['/dove-comprare', t('home.b2cTitle'), t('home.b2cText'), t('common.findRetailer'), 'b2c'],
    ['/professionisti/rivenditori', t('home.b2bTitle'), t('home.b2bText'), t('common.becomeReseller'), 'b2b'],
  ];

  return (
    <section data-testid="home-paths" className="py-16 md:py-24">
      <div className="wrap">
        <h2 className="mb-10 text-[30px] leading-[1.06] md:text-[40px] lg:text-[52px]">{t('home.labelPaths')}</h2>
        <div className="flex flex-col gap-3 md:flex-row md:gap-4">
          {paths.map(([to, title, text, cta, id]) => (
            <motion.div
              key={to}
              initial={{ flexGrow: 1 }}
              whileHover={{ flexGrow: 1.5 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="min-w-0 flex-1 [@media(hover:none)]:!flex-grow"
            >
              <Link
                to={to}
                data-testid={`path-${id}`}
                className={`group relative flex h-full min-h-[240px] flex-col justify-between overflow-hidden p-7 transition-colors duration-500 md:min-h-[300px] md:p-12 ${
                  id === 'b2b'
                    ? 'bg-inchiostro text-carta hover:bg-notte'
                    : 'bg-carta-2 hover:bg-carta-3'
                }`}
              >
                <div>
                  <h3 className="max-w-[16ch] text-[24px] leading-[1.05] md:text-[36px]">
                    {title}
                  </h3>
                  <p className={`prosa mt-5 ${id === 'b2b' ? '!text-carta/75' : ''}`}>
                    {text}
                  </p>
                </div>
                <span
                  className={`mt-8 inline-flex items-center gap-3 text-[16px] font-bold ${
                    id === 'b2b' ? 'text-ocra' : 'text-marsala-dark'
                  }`}
                >
                  {cta}
                  <ArrowUpRight
                    size={16}
                    strokeWidth={2}
                    className="transition-transform duration-500 group-hover:translate-x-1.5 group-hover:-translate-y-1.5"
                  />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
